import { LimitWarning } from './LimitWarning';
import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { formatINR } from '@/lib/format';
import { Crown, ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-react';

const POLICY_ITEMS = [
    { key: 'refund', href: '/policies/refund', label: 'Refund Policy', prefix: 'I have read and accept the ', suffix: '.' },
    { key: 'privacy', href: '/policies/privacy', label: 'Privacy Policy', prefix: 'I accept the ', suffix: '.' },
    { key: 'fairuse', href: '/policies/fair-use', label: 'Fair Use Policy', prefix: 'I accept the ', suffix: ', understand digital goods are non-reversible, and confirm this is for entertainment use only.' },
];

export function NobleRechargeCard({
    platformMeta,
    tiers = [],
    discountPct = 0,
    tier,
    onTierChange,
    mode,
    onModeChange,
    currentNoble,
    inAppId,
    onInAppIdChange,
    onInAppIdBlur,
    verifying,
    recipientInfo,
    verifyError,
    policy,
    onPolicyChange,
    allPoliciesAccepted,
    gateways = [],
    gateway,
    onGatewayChange,
    loading,
    onPay,
    maxLimit = 15000,
}) {
    const selected = tiers.find((t) => t.key === tier);
    const price = selected ? Number(selected.price?.[mode] || 0) : 0;
    const listPrice = selected ? Number(selected.list_price?.[mode] || 0) : 0;
    const hasDiscount = discountPct > 0 && listPrice > price && price > 0;
    const payGateway = gateways.find((g) => g.key === gateway);
    const gatewayNotReady = payGateway && !payGateway.implemented;
    const modeAvailable = (m) => selected && Number(selected.price?.[m] || 0) > 0;

    return (
        <Card className="mt-6 p-4 sm:p-5" data-testid="noble-recharge-card">
            {tiers.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center" data-testid="noble-empty">
                    Noble titles aren't available right now. Please check back soon.
                </div>
            ) : (
                <>
                    {currentNoble && currentNoble.has_noble && (
                        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-center gap-2 text-sm" data-testid="noble-current-tier">
                            <Crown className="h-4 w-4 text-amber-600" />
                            <span className="text-amber-900">
                                Recipient currently holds <strong>{currentNoble.tier_label || 'a noble title'}</strong>
                                {currentNoble.expire_at ? ` (expires ${new Date(currentNoble.expire_at * 1000).toLocaleDateString()})` : ''}.
                                {' '}We've selected <strong>Renewal</strong> for you — switch the title above to upgrade instead.
                            </span>
                        </div>
                    )}
                    {currentNoble && currentNoble.has_noble === false && inAppId && inAppId.trim() && (
                        <div className="mb-4 rounded-xl border border-border bg-secondary/40 p-3 text-sm text-muted-foreground" data-testid="noble-current-none">
                            This account has no active noble title — choose a title below to activate it.
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Choose a noble title</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="noble-tier-grid">
                            {tiers.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => onTierChange(t.key)}
                                    className={`px-3 py-3 rounded-xl border text-sm font-medium transition-colors flex flex-col items-center gap-1 ${tier === t.key ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-foreground border-border hover:border-amber-400'}`}
                                    data-testid={`noble-tier-option-${t.key}`}
                                >
                                    <Crown className="w-4 h-4" />
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</Label>
                        <div className="flex gap-2 mt-2" data-testid="noble-mode-toggle">
                            <button
                                type="button"
                                disabled={!modeAvailable('first')}
                                onClick={() => onModeChange('first')}
                                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 ${mode === 'first' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                data-testid="noble-mode-first"
                            >
                                First-time
                            </button>
                            <button
                                type="button"
                                disabled={!modeAvailable('renew')}
                                onClick={() => onModeChange('renew')}
                                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 ${mode === 'renew' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                data-testid="noble-mode-renew"
                            >
                                Renewal
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="noble-inappid">Your {platformMeta?.name || 'Qwick Live'} in-app ID</Label>
                            <Input
                                id="noble-inappid"
                                data-testid="noble-inapp-id-input"
                                placeholder="Enter your in-app ID exactly"
                                value={inAppId}
                                onChange={(e) => onInAppIdChange(e.target.value)}
                                onBlur={() => onInAppIdBlur && onInAppIdBlur()}
                            />
                            {verifying && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="noble-verifying">
                                    <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                                    Fetching account…
                                </div>
                            )}
                            {!verifying && recipientInfo && (
                                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2" data-testid="noble-recipient-info">
                                    {recipientInfo.avatar
                                        ? <img src={recipientInfo.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                                        : <div className="h-8 w-8 rounded-full bg-emerald-200" />}
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-emerald-900 truncate" data-testid="noble-recipient-nick">
                                            {recipientInfo.nick || 'Account found'}
                                        </div>
                                        <div className="text-[11px] text-emerald-700 font-mono">ID: {recipientInfo.vid || inAppId}</div>
                                    </div>
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600 ml-auto flex-shrink-0" />
                                </div>
                            )}
                            {!verifying && !recipientInfo && verifyError && (
                                <div className="text-xs text-rose-600" data-testid="noble-verify-error">{verifyError}</div>
                            )}
                            <div className="text-xs text-muted-foreground">We auto-verify your ID so the title is activated on the right account.</div>
                        </div>

                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="noble-order-summary">
                            <div className="text-xs text-amber-700">Order summary</div>
                            <div className="mt-1 text-sm text-amber-900 flex items-center gap-1.5">
                                <Crown className="h-4 w-4" /> {selected?.label || '—'} · {mode === 'renew' ? 'Renewal' : 'First-time'}
                            </div>
                            <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-amber-900" data-testid="noble-price-value">
                                {price > 0 ? formatINR(price) : '—'}
                                {hasDiscount && <span className="ml-2 text-sm font-normal text-amber-700/70 line-through" data-testid="noble-list-price">{formatINR(listPrice)}</span>}
                            </div>
                            {hasDiscount && (
                                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid="noble-savings-badge">
                                    You save {formatINR(listPrice - price)} ({discountPct}%)
                                </div>
                            )}
                            {price <= 0 && <div className="text-xs text-rose-600 mt-1">This option isn't priced. Pick another.</div>}
                        </div>
                    </div>

                    {gateways.length > 1 && (
                        <div className="mt-5" data-testid="noble-gateway-selector">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pay using</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {gateways.map((g) => (
                                    <button
                                        key={g.key}
                                        type="button"
                                        onClick={() => onGatewayChange && onGatewayChange(g.key)}
                                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${gateway === g.key ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                        data-testid={`noble-gateway-option-${g.key}`}
                                    >
                                        {g.name}
                                        {!g.implemented && <span className="ml-1.5 text-[10px] uppercase opacity-70">Coming soon</span>}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-5 p-4 rounded-xl border border-border bg-secondary/30">
                        <div className="flex items-start gap-2 text-[13px] mb-3">
                            <AlertCircle className="h-4 w-4 mt-0.5 text-[#B45309]" />
                            <div className="text-foreground/85">Digital goods are non-reversible once credited. This service is for entertainment use only.</div>
                        </div>
                        <div className="space-y-2">
                            {POLICY_ITEMS.map(({ key, href, label, prefix, suffix }) => (
                                <label key={key} className="flex items-start gap-2 text-[13px] cursor-pointer">
                                    <Checkbox checked={!!policy[key]} onCheckedChange={(v) => onPolicyChange(key, !!v)} data-testid={`noble-policy-${key}-checkbox`} />
                                    <span>{prefix}<a className="text-[#1F9D8A] underline" href={href} target="_blank" rel="noreferrer">{label}</a>{suffix}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {price > maxLimit && <LimitWarning testid="noble-limit-warning" amount={price} />}
                    <Button
                        disabled={loading || !selected || price <= 0 || price > maxLimit || !inAppId.trim() || !recipientInfo || !allPoliciesAccepted || gatewayNotReady}
                        onClick={onPay}
                        className="mt-5 w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50"
                        data-testid="noble-pay-button"
                    >
                        {loading ? 'Processing…' : (
                            <span className="flex items-center gap-2">
                                <Crown className="h-4 w-4" /> Pay {price > 0 ? formatINR(price) : ''} & activate noble
                            </span>
                        )}
                    </Button>
                    {!recipientInfo && inAppId.trim() && !verifying && (
                        <div className="mt-2 text-[11px] text-amber-600 text-center" data-testid="noble-verify-hint">
                            Verify your in-app ID above to enable payment.
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}

export default NobleRechargeCard;
